// contexts/SettingsContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const SettingsContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Varsayılan ayarlar - backend olmadan da çalışabilir
const DEFAULT_SETTINGS = {
  target_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 gün sonra
  timezone: 'Europe/Istanbul',
  show_label: true,
  label_text: 'COUNTDOWN',
  show_breakdown: true,
  digit_count: 'auto',
  background_color: '#0a0a0b',
  card_color: '#0f0f10',
  text_color: '#e8dcc4',
  split_line_color: '#2a2a2d',
  label_color: '#e8dcc4',
  logo_color: '#e8dcc4',
  sound_enabled: false,
  master_volume: 0.5,
  clack_enabled: true,
  flutter_enabled: true,
  thud_enabled: true,
  particle_effects_enabled: true,
  ambient_particles_enabled: true,
  ambient_particles_count: 25,
  milestone_celebrations_enabled: true,
  celebration_cooldown_hours: 6,
  celebration_type: 'confetti',
  final_countdown_enabled: true,
  final_countdown_threshold: 1000,
  final_countdown_effects_enabled: true,
  zero_countdown_effects_enabled: true,
  zero_behavior: 'message',
  zero_message: "Time's up!",
  milestone_1m: true,
  milestone_500k: true,
  milestone_100k: true,
  milestone_10k: true,
  milestone_1k: true,
  milestone_100: true,
  milestone_10: true
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

// Başlangıç ayarlarını hesapla - localStorage veya varsayılan
const getInitialSettings = () => {
  try {
    const saved = localStorage.getItem('countdown_settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      const merged = { ...DEFAULT_SETTINGS, ...parsed };
      // Geçmiş tarih kontrolü
      if (merged.target_date && new Date(merged.target_date) < new Date()) {
        merged.target_date = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      }
      return merged;
    }
  } catch {}
  return DEFAULT_SETTINGS;
};

export const SettingsProvider = ({ children }) => {
  // Anında varsayılan ayarlarla başla - loading ekranı YOK!
  const [settings, setSettings] = useState(getInitialSettings);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [isSettingsReady, setIsSettingsReady] = useState(() => {
    // localStorage varsa hemen ready
    return !!localStorage.getItem('countdown_settings');
  });

  // Fetch settings - localStorage öncelikli, backend sadece ilk yüklemede
  const fetchSettings = async (forceBackend = false) => {
    try {
      setError(null);
      
      // localStorage'daki mevcut ayarları al
      const localSettings = localStorage.getItem('countdown_settings');
      const currentLocal = localSettings ? JSON.parse(localSettings) : null;
      
      // Eğer localStorage varsa ve force değilse, backend'i atla
      if (currentLocal && !forceBackend) {
        setIsSettingsReady(true);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/settings`, { timeout: 3000 });
        
        // API'den HTML (hata sayfası) dönüp dönmediğini kontrol et
        const contentType = response.headers['content-type'];
        if (contentType && contentType.includes('text/html')) {
          throw new Error('API endpoint bulunamadı');
        }

        const settingsData = response.data;
        
        // Eğer data string ise hata fırlat
        if (typeof settingsData === 'string') {
          throw new Error('API geçersiz veri formatı döndürdü');
        }
        
        // localStorage varsa onu koru, yoksa backend'den al
        const mergedSettings = currentLocal 
          ? { ...DEFAULT_SETTINGS, ...settingsData, ...currentLocal }
          : { ...DEFAULT_SETTINGS, ...settingsData };
        
        setSettings(mergedSettings);
        localStorage.setItem('countdown_settings', JSON.stringify(mergedSettings));
        
      } catch (apiError) {
        // Backend'e ulaşılamadı - mevcut ayarlar zaten yüklü, sessizce devam et
        console.warn('Backend\'e bağlanılamadı, mevcut ayarlar kullanılıyor:', apiError.message);
      }
      // Backend response geldi (başarılı veya başarısız) - artık ready
      setIsSettingsReady(true);
    } catch (err) {
      console.error('Settings yüklenirken hata:', err);
      setIsSettingsReady(true);
    }
  };

  // Update settings - backend olmadan da çalışır (localStorage'a kaydeder)
  const updateSettings = async (updates) => {
    try {
      const newSettings = { ...settings, ...updates };
      
      // Önce yerel olarak güncelle
      setSettings(newSettings);
      localStorage.setItem('countdown_settings', JSON.stringify(newSettings));
      
      // Backend varsa oraya da kaydet
      if (token) {
        try {
          await axios.put(`${API}/settings`, updates, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 3000
          });
        } catch (apiError) {
          console.warn('Backend\'e kaydedilemedi, sadece yerel kaydedildi:', apiError.message);
        }
      }
      
      return newSettings;
    } catch (err) {
      console.error('Failed to update settings:', err);
      throw err;
    }
  };

  // Login - backend olmadan da çalışır (varsayılan şifre: admin123)
  const login = async (password) => {
    try {
      // Önce backend'e dene
      try {
        const response = await axios.post(`${API}/auth/login`, { password }, { timeout: 3000 });
        const newToken = response.data.access_token;
        setToken(newToken);
        localStorage.setItem('admin_token', newToken);
        setIsAuthenticated(true);
        return true;
      } catch (apiError) {
        // Backend yoksa yerel doğrulama yap
        console.warn('Backend\'e bağlanılamadı, yerel doğrulama yapılıyor');
        const localPassword = 'admin123'; // Varsayılan şifre
        if (password === localPassword) {
          const fakeToken = 'local_admin_token_' + Date.now();
          setToken(fakeToken);
          localStorage.setItem('admin_token', fakeToken);
          setIsAuthenticated(true);
          return true;
        } else {
          throw new Error('Geçersiz şifre');
        }
      }
    } catch (err) {
      console.error('Login failed:', err);
      throw err;
    }
  };

  // Logout
  const logout = () => {
    setToken(null);
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  // Verify token on load
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        // Yerel token ise doğrudan kabul et
        if (token.startsWith('local_admin_token_')) {
          setIsAuthenticated(true);
          return;
        }
        
        try {
          await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 3000
          });
          setIsAuthenticated(true);
        } catch (err) {
          // Backend yoksa yerel token olarak kabul et
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
            setIsAuthenticated(true);
          } else {
            console.error('Token verification failed:', err);
            logout();
          }
        }
      }
    };
    verifyToken();
  }, [token]);

  // Initial fetch - sadece bir kere, localStorage öncelikli
  useEffect(() => {
    fetchSettings();
  }, []);

  // Settings ASLA null olamaz - her zaman en azından varsayılan değerler olacak
  const safeSettings = settings || DEFAULT_SETTINGS;

  const value = {
    settings: safeSettings,
    loading: false, // Her zaman false - loading ekranı asla gösterilmeyecek
    error,
    isAuthenticated,
    isSettingsReady, // Backend response geldi mi?
    fetchSettings,
    updateSettings,
    login,
    logout
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};